import { ComponentType } from 'react';
import { AdjustmentsHorizontalIcon, CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';

import { PropertyFilter, PropertyFilterType } from 'support/types/filters';

import { CountBadge } from './components/CountBadge';

export type IconComponent = ComponentType<{ className?: string }>;

// Checkbox filters render standalone next to the menu (not as a menu row); search/hidden never render.
export const HIDDEN_TYPES = [PropertyFilterType.search, PropertyFilterType.hidden, PropertyFilterType.checkbox];

const DEFAULT_ICONS: Partial<Record<PropertyFilterType, IconComponent>> = {
  [PropertyFilterType.dropdown]: AdjustmentsHorizontalIcon,
  [PropertyFilterType.date]: CalendarIcon,
};

// Falls back to a string placeholder when a filter has no explicit label (e.g. date filters that only set placeholder).
export const getFilterLabel = (filter: PropertyFilter): string | undefined =>
  filter.label ?? (typeof filter.placeholder === 'string' ? filter.placeholder : undefined);

export const getActiveCount = (filter: PropertyFilter): number => {
  if (!filter.value) {
    return 0;
  }
  if (filter.type === PropertyFilterType.date) {
    return 1;
  }
  return filter.value.split(',').filter(Boolean).length;
};

// Counts a filter as active per category (a multi-select with 3 values still counts as one active filter).
export const isFilterActive = (filter: PropertyFilter): boolean => {
  if (filter.type === PropertyFilterType.checkbox) {
    return filter.value === 'true';
  }
  return Boolean(filter.value);
};

export const resolveTriggerIcon = (activeCount: number): IconComponent => {
  if (activeCount > 0) {
    const TriggerCountIcon = ({ className }: { className?: string }) => (
      <CountBadge className={className} count={activeCount} />
    );
    TriggerCountIcon.displayName = 'FiltersTriggerCountIcon';
    return TriggerCountIcon;
  }
  return FunnelIcon;
};

export const resolveRowIcon = (filter: PropertyFilter, count: number): IconComponent | undefined => {
  if (count > 0) {
    const CountIcon = ({ className }: { className?: string }) => <CountBadge className={className} count={count} />;
    CountIcon.displayName = 'FilterCountIcon';
    return CountIcon;
  }
  return filter.Icon ?? DEFAULT_ICONS[filter.type];
};
